import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { companySettingsKeys } from '../../company-settings/api/query-keys';
import type { InvitationMethod } from '../../company-settings/api/user-management';
import { useInvitationOptions } from '../../company-settings/api/user-management/use-invitation-options';
import type { InviteTeamMemberFormData } from '../../company-settings/tabs/organization-tab/users-and-roles/types';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

type UseInviteTeamMemberParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
};

type UseInviteTeamMemberReturn = {
    readonly availableMethods: readonly InvitationMethod[] | undefined;
    readonly closeInviteDialog: () => void;
    readonly handleInvite: (data: InviteTeamMemberFormData) => Promise<void>;
    readonly isInviteDialogOpen: boolean;
    readonly openInviteDialog: (() => void) | undefined;
};

export const useInviteTeamMember = (params?: UseInviteTeamMemberParams): UseInviteTeamMemberReturn => {
    const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
    const { showErrorMessageSnackbar, showSuccessSnackbar } = useSnackbars();
    const { inviteAndAssignToLevel, inviteAndAssignToRole } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const { data: invitationOptions } = useInvitationOptions();
    const { applicationState } = useApplicationContext();
    const isEnabled = applicationState.featureFlagService.isFeatureFlagEnabled('user-management-settings');

    const inviteAndAssignMutation = useMutation({
        mutationFn: async (formData: InviteTeamMemberFormData) => {
            if (params === undefined) {
                throw new Error('Missing architecture context for invite-and-assign');
            }

            const request = {
                emailAddress: formData.email,
                firstName: formData.firstName,
                inviteLanguage: formData.inviteLanguage,
                lastName: formData.lastName,
                roles: formData.roles as readonly string[],
            };

            const result =
                params.itemType === 'job-level'
                    ? await inviteAndAssignToLevel({
                          jobArchitectureId: params.architectureId,
                          levelId: params.itemId,
                          request,
                      })
                    : await inviteAndAssignToRole({
                          jobArchitectureId: params.architectureId,
                          request,
                          roleId: params.itemId,
                      });

            if (result.isErr) {
                throw result.error;
            }
        },
        onSuccess: async () => {
            if (params !== undefined) {
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: companySettingsKeys.organizationUsers() }),
                    queryClient.invalidateQueries({
                        queryKey:
                            params.itemType === 'job-level'
                                ? jobArchitectureKeys.assignableLevelReviewers(params.architectureId, params.itemId, '')
                                : jobArchitectureKeys.assignableRoleReviewers(params.architectureId, params.itemId, ''),
                    }),
                ]);
            }
        },
    });

    const openInviteDialog = (): void => {
        setIsInviteDialogOpen(true);
    };

    const closeInviteDialog = (): void => {
        setIsInviteDialogOpen(false);
    };

    const handleInvite = async (formData: InviteTeamMemberFormData): Promise<void> => {
        try {
            await inviteAndAssignMutation.mutateAsync(formData);
            showSuccessSnackbar('pages.company-settings.users-and-roles.success.invite-sent');
        } catch {
            showErrorMessageSnackbar('pages.company-settings.users-and-roles.errors.invite-failed');
        }
    };

    return {
        availableMethods: isEnabled ? invitationOptions?.availableMethods : undefined,
        closeInviteDialog,
        handleInvite,
        isInviteDialogOpen,
        openInviteDialog: isEnabled ? openInviteDialog : undefined,
    };
};
